import { useMemo, useState } from 'react';

import classNames from 'classnames';

import { Button } from 'fluxo-ui';
import { CheckIcon, StarFilledIcon } from 'fluxo-ui/icons';

import { LiveQuizSessionSummary } from '../../../types/index.ts';
import useQuestionCountdown from '../../hooks/useQuestionCountdown';
import { useLiveQuizStore } from '../../store/liveQuizStore';

import ConnectionBadge from './ConnectionBadge';
import Leaderboard from './Leaderboard';
import { optionAccents } from './optionAccents';
import QuestionTimer from './QuestionTimer';

interface LiveQuizParticipantProps {
    session: LiveQuizSessionSummary;
}

const LiveQuizParticipant: React.FC<LiveQuizParticipantProps> = ({ session }) => {
    const { question, reveal, leaderboard, personalResult, answeredCurrentQuestion, myParticipantId, connectionState, submitAnswer } =
        useLiveQuizStore();

    const { secondsRemaining, progress } = useQuestionCountdown(question);
    const [selectedOptionIds, setSelectedOptionIds] = useState<string[]>([]);

    const isMultiSelect = question?.type === 'mcq_multiple';

    const myEntry = useMemo(
        () => leaderboard.find((entry) => entry.participantId === myParticipantId),
        [leaderboard, myParticipantId],
    );

    const score = personalResult?.score ?? myEntry?.score ?? 0;
    const streak = personalResult?.streak ?? myEntry?.streak ?? 0;

    const toggleOption = (optionId: string) => {
        if (answeredCurrentQuestion) {
            return;
        }
        if (isMultiSelect) {
            setSelectedOptionIds((prev) =>
                prev.includes(optionId) ? prev.filter((id) => id !== optionId) : [...prev, optionId],
            );
            return;
        }
        setSelectedOptionIds([optionId]);
        submitAnswer([optionId]);
    };

    const confirmMultiSelect = () => {
        if (selectedOptionIds.length > 0) {
            submitAnswer(selectedOptionIds);
        }
    };

    return (
        <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex flex-col">
            <header className="border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
                <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
                    <ConnectionBadge state={connectionState} />
                    <div className="flex items-center gap-4">
                        {streak > 1 && (
                            <span className="flex items-center gap-1 text-sm font-semibold text-orange-600 dark:text-orange-400">
                                <StarFilledIcon className="w-4 h-4" />
                                {streak}
                            </span>
                        )}
                        <span className="text-sm text-slate-500 dark:text-slate-400">
                            Score <span className="ml-1 font-bold tabular-nums text-slate-900 dark:text-white">{score}</span>
                        </span>
                    </div>
                </div>
            </header>

            <main className="flex-1 max-w-3xl w-full mx-auto px-4 py-6">
                {session.status === 'waiting' && (
                    <div className="grid place-items-center min-h-[60vh] text-center">
                        <div>
                            <div className="mx-auto mb-5 w-10 h-10 rounded-full border-2 border-slate-200 dark:border-slate-700 border-t-teal-500 animate-spin" />
                            <h1 className="text-lg font-semibold text-slate-900 dark:text-white">You're in!</h1>
                            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                                Waiting for the host to start the quiz.
                            </p>
                        </div>
                    </div>
                )}

                {session.status === 'question' && question && (
                    <div className="space-y-6">
                        <div className="flex items-start justify-between gap-4">
                            <div className="min-w-0">
                                <p className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
                                    Question {question.questionIndex + 1} of {question.totalQuestions}
                                </p>
                                <h1 className="mt-2 text-xl sm:text-2xl font-semibold leading-snug text-slate-900 dark:text-white">
                                    {question.text || question.title}
                                </h1>
                            </div>
                            <QuestionTimer secondsRemaining={secondsRemaining} progress={progress} size="sm" />
                        </div>

                        {answeredCurrentQuestion ? (
                            <div className="rounded-lg border border-emerald-200 dark:border-emerald-900 bg-emerald-50 dark:bg-emerald-950/40 p-6 text-center">
                                <CheckIcon className="mx-auto mb-2 w-8 h-8 text-emerald-600 dark:text-emerald-400" />
                                <p className="font-medium text-emerald-900 dark:text-emerald-100">Answer locked in</p>
                                <p className="mt-1 text-sm text-emerald-700 dark:text-emerald-300">
                                    Hang tight while everyone else answers.
                                </p>
                            </div>
                        ) : (
                            <>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    {question.options.map((option, index) => {
                                        const accent = optionAccents[index % optionAccents.length];
                                        const isSelected = selectedOptionIds.includes(option.id);

                                        return (
                                            <button
                                                key={option.id}
                                                type="button"
                                                onClick={() => toggleOption(option.id)}
                                                className={classNames(
                                                    'cursor-pointer flex items-center gap-3 rounded-lg p-4 text-left text-white font-medium transition-transform',
                                                    accent.solid,
                                                    accent.hover,
                                                    'hover:-translate-y-0.5 active:translate-y-0',
                                                    { 'ring-4 ring-offset-2 ring-offset-slate-50 dark:ring-offset-slate-950': isSelected },
                                                    isSelected ? accent.ring : '',
                                                )}
                                            >
                                                <span className="grid place-items-center w-7 h-7 shrink-0 rounded bg-white/25 text-sm font-bold">
                                                    {String.fromCharCode(65 + index)}
                                                </span>
                                                <span className="min-w-0 flex-1">{option.text}</span>
                                                {isSelected && <CheckIcon className="w-5 h-5 shrink-0" />}
                                            </button>
                                        );
                                    })}
                                </div>

                                {isMultiSelect && (
                                    <Button
                                        label={`Submit ${selectedOptionIds.length > 0 ? `(${selectedOptionIds.length} selected)` : ''}`}
                                        variant="primary"
                                        size="lg"
                                        className="w-full"
                                        disabled={selectedOptionIds.length === 0}
                                        onClick={confirmMultiSelect}
                                    />
                                )}
                            </>
                        )}
                    </div>
                )}

                {session.status === 'reveal' && reveal && (
                    <div className="space-y-5">
                        <div
                            className={classNames('rounded-lg border p-6 text-center', {
                                'border-emerald-200 dark:border-emerald-900 bg-emerald-50 dark:bg-emerald-950/40':
                                    personalResult?.isCorrect,
                                'border-rose-200 dark:border-rose-900 bg-rose-50 dark:bg-rose-950/40':
                                    personalResult && !personalResult.isCorrect,
                                'border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900': !personalResult,
                            })}
                        >
                            <p
                                className={classNames('text-xl font-semibold', {
                                    'text-emerald-900 dark:text-emerald-100': personalResult?.isCorrect,
                                    'text-rose-900 dark:text-rose-100': personalResult && !personalResult.isCorrect,
                                    'text-slate-900 dark:text-white': !personalResult,
                                })}
                            >
                                {!personalResult?.answered
                                    ? 'No answer submitted'
                                    : personalResult.isCorrect
                                      ? 'Correct!'
                                      : 'Not this time'}
                            </p>
                            {personalResult && personalResult.pointsAwarded > 0 && (
                                <p className="mt-1 font-medium text-emerald-700 dark:text-emerald-300 tabular-nums">
                                    +{personalResult.pointsAwarded} points
                                </p>
                            )}
                            {personalResult && (
                                <p className="mt-3 text-sm text-slate-600 dark:text-slate-400 tabular-nums">
                                    Rank #{personalResult.rank} · {personalResult.score} points
                                </p>
                            )}
                        </div>

                        <section className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5">
                            <h2 className="mb-3 text-sm font-semibold text-slate-900 dark:text-white">Correct answer</h2>
                            <ul className="space-y-2">
                                {reveal.stats.optionDistribution
                                    .filter((option) => option.isCorrect)
                                    .map((option) => (
                                        <li
                                            key={option.optionId}
                                            className="flex items-center gap-2 rounded border border-emerald-200 dark:border-emerald-900 bg-emerald-50 dark:bg-emerald-950/40 px-3 py-2 text-sm font-medium text-emerald-900 dark:text-emerald-100"
                                        >
                                            <CheckIcon className="w-4 h-4 shrink-0" />
                                            {option.text}
                                        </li>
                                    ))}
                            </ul>
                        </section>
                    </div>
                )}

                {(session.status === 'leaderboard' || session.status === 'ended') && (
                    <section className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5">
                        <h2 className="mb-5 text-sm font-semibold text-slate-900 dark:text-white">
                            {session.status === 'ended' ? 'Final results' : 'Standings'}
                        </h2>
                        <Leaderboard
                            entries={leaderboard}
                            highlightParticipantId={myParticipantId}
                            showPodium={session.status === 'ended'}
                            limit={20}
                        />
                    </section>
                )}
            </main>
        </div>
    );
};

export default LiveQuizParticipant;
